import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from 'components/atoms/Button';
import Checkbox from 'components/atoms/Checkbox';
import Label from 'components/foundations/Label';
import TextField from 'components/molecules/TextField';
import Select from 'components/widgets/Select';
import { AVAILABLE_FIELDS_LIST, SELECTED_FIELDS_LIST } from '../constants';
import css from '../ExportLogs.css';

const presetSelectStyles = {
    control: base => ({ ...base, minHeight: 35, height: 35 }),
    valueContainer: base => ({
        ...base,
        height: 35,
        flexWrap: 'nowrap',
        overflow: 'hidden',
        paddingTop: 0,
        paddingBottom: 0,
    }),
    placeholder: base => ({ ...base, whiteSpace: 'nowrap' }),
    singleValue: base => ({ ...base, whiteSpace: 'nowrap' }),
};

const normalizeSearch = value => (value || '').toLowerCase();

const fieldShape = PropTypes.shape({
    field: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
});

const presetShape = PropTypes.shape({
    text: PropTypes.string.isRequired,
    value: PropTypes.string,
    label: PropTypes.string,
});

const renderHighlighted = (text, search) => {
    if (!search) {
        return text;
    }

    const lowerText = text.toLowerCase();
    const lowerSearch = search.toLowerCase();
    const index = lowerText.indexOf(lowerSearch);
    if (index === -1) {
        return text;
    }

    return (
        <>
            {text.slice(0, index)}
            <span className={css.highlightedSearch}>{text.slice(index, index + search.length)}</span>
            {text.slice(index + search.length)}
        </>
    );
};

function FieldPanel({
    title,
    list,
    filter,
    onFilterChange,
    disabled,
    onDragStart,
    onDrop,
    onDragOver,
    onRemove,
    searchPlaceholder,
}) {
    const listRef = useRef(null);
    const filteredList = useMemo(() => {
        const normalized = normalizeSearch(filter);
        return list
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.display.toLowerCase().includes(normalized));
    }, [filter, list]);

    const getDropIndex = event => {
        const exactRow = event.target.closest?.('[data-field-index]');
        const clientY = event.clientY ?? event.nativeEvent?.clientY;
        if (!Number.isFinite(clientY)) {
            return exactRow ? Number(exactRow.dataset.fieldIndex) : undefined;
        }

        const rows = Array.from(listRef.current?.querySelectorAll('[data-field-index]') || []);
        const targetRow = rows.find(row => {
            const rect = row.getBoundingClientRect();
            return clientY < rect.top + rect.height / 2;
        });

        if (!targetRow) {
            return undefined;
        }

        return Number(targetRow.dataset.fieldIndex);
    };

    const handleDrop = event => {
        event.preventDefault();
        event.stopPropagation();
        onDrop(getDropIndex(event));
    };

    return (
        <div
            className={css.fieldsPanel}
            onDrop={handleDrop}
            onDragOver={onDragOver}
        >
            <div className={classNames(css.fieldsPanelHeading, { [css.disabled]: disabled })}>
                <div className={css.fieldsPanelTitle}>
                    {title} ({list.length})
                </div>
            </div>
            <div className={classNames(css.fieldsPanelBody, { [css.disabled]: disabled })}>
                <TextField
                    containerClass={css.fieldSearchContainer}
                    inputClassName={classNames(css.fieldSearch, { [css.disabled]: disabled })}
                    placeholder={searchPlaceholder}
                    value={filter}
                    disabled={disabled}
                    onChange={onFilterChange}
                />
                <ul className={css.fieldsList} ref={listRef}>
                    {filteredList.map(({ item, index }) => (
                        <li className={css.fieldsItem} data-field-index={index} key={item.field}>
                            <button
                                type="button"
                                className={classNames(css.dragItem, { [css.disabled]: disabled })}
                                draggable={!disabled}
                                disabled={disabled}
                                title={item.display}
                                onDragStart={() => onDragStart(item)}
                                onDragOver={event => event.preventDefault()}
                                onDoubleClick={() => onRemove(item)}
                            >
                                {renderHighlighted(item.display, filter)}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

FieldPanel.propTypes = {
    title: PropTypes.string.isRequired,
    list: PropTypes.arrayOf(fieldShape).isRequired,
    filter: PropTypes.string.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
    onDragStart: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
    onDragOver: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
};

function FieldSelector({
    available,
    selected,
    availableFilter,
    selectedFilter,
    onAvailableFilterChange,
    onSelectedFilterChange,
    onMoveField,
    onClearAll,
    adminMode,
    apsalarEnabled,
    exportAll,
    onExportAllChange,
    presetOptions,
    onLoadPreset,
    translate,
}) {
    const [dragged, setDragged] = useState(null);
    const disabled = !!exportAll;
    const hasPresets = adminMode && presetOptions.length > 0;

    const handleDrop = (targetList, targetIndex) => {
        if (!dragged) {
            return;
        }
        onMoveField(dragged.item, dragged.source, targetList, targetIndex);
        setDragged(null);
    };

    const moveByDoubleClick = (item, source) => {
        onMoveField(item, source, source === AVAILABLE_FIELDS_LIST ? SELECTED_FIELDS_LIST : AVAILABLE_FIELDS_LIST);
    };

    return (
        <div className={css.formRow}>
            <div className={classNames(css.formLabel, css.fieldSelectorLabel)}>
                <Label text="STATIC.PAGES.EXPORT_LOGS.FIELDS_TO_EXPORT" className={css.formLabelText} />
                <span>{translate('STATIC.PAGES.EXPORT_LOGS.FIELDS_TO_EXPORT_HELP')}</span>
            </div>
            <div className={css.fieldsSelector} id="fieldSelector">
                <div className={css.fieldsPanelWrap}>
                    <div className={css.fieldsPanelHeader}>
                        <div>
                            {translate('STATIC.PAGES.EXPORT_LOGS.AVAILABLE_FIELDS')} ({available.length})
                        </div>
                        {apsalarEnabled && adminMode && (
                            <Checkbox
                                className={css.exportAll}
                                checked={exportAll}
                                label={translate('STATIC.PAGES.EXPORT_LOGS.EXPORT_ALL_FIELDS')}
                                onChange={onExportAllChange}
                            />
                        )}
                    </div>
                    <FieldPanel
                        title={translate('STATIC.PAGES.EXPORT_LOGS.AVAILABLE_FIELDS')}
                        list={available}
                        filter={availableFilter}
                        onFilterChange={onAvailableFilterChange}
                        disabled={disabled}
                        onDragStart={item => setDragged({ item, source: AVAILABLE_FIELDS_LIST })}
                        onDrop={targetIndex => handleDrop(AVAILABLE_FIELDS_LIST, targetIndex)}
                        onDragOver={event => event.preventDefault()}
                        onRemove={item => moveByDoubleClick(item, AVAILABLE_FIELDS_LIST)}
                        searchPlaceholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.FIELD_SEARCH"
                    />
                </div>
                <div className={css.fieldsPanelWrap}>
                    <div className={classNames(css.fieldsPanelHeader, { [css.disabled]: disabled })}>
                        <div>
                            {translate('STATIC.PAGES.EXPORT_LOGS.SELECTED_FIELDS')} ({selected.length})
                        </div>
                        <div className={css.selectedActions}>
                            {hasPresets && (
                                <Select
                                    className={css.presetSelect}
                                    placeholder={translate('STATIC.PAGES.EXPORT_LOGS.LOAD_PRESET')}
                                    options={presetOptions}
                                    onChange={onLoadPreset}
                                    isDisabled={disabled}
                                    value={null}
                                    styles={presetSelectStyles}
                                />
                            )}
                            <Button
                                type="link"
                                className={classNames(css.textAction, {
                                    [css.hidden]: selected.length === 0,
                                    [css.disabled]: disabled,
                                })}
                                disabled={disabled || selected.length === 0}
                                onClick={onClearAll}
                                text="STATIC.BUTTONS.CLEAR_ALL"
                            >
                            </Button>
                        </div>
                    </div>
                    <FieldPanel
                        title={translate('STATIC.PAGES.EXPORT_LOGS.SELECTED_FIELDS')}
                        list={selected}
                        filter={selectedFilter}
                        onFilterChange={onSelectedFilterChange}
                        disabled={disabled}
                        onDragStart={item => setDragged({ item, source: SELECTED_FIELDS_LIST })}
                        onDrop={targetIndex => handleDrop(SELECTED_FIELDS_LIST, targetIndex)}
                        onDragOver={event => event.preventDefault()}
                        onRemove={item => moveByDoubleClick(item, SELECTED_FIELDS_LIST)}
                        searchPlaceholder="STATIC.PAGES.EXPORT_LOGS.PLACEHOLDERS.FIELD_SEARCH"
                    />
                </div>
            </div>
        </div>
    );
}

FieldSelector.propTypes = {
    available: PropTypes.arrayOf(fieldShape).isRequired,
    selected: PropTypes.arrayOf(fieldShape).isRequired,
    availableFilter: PropTypes.string.isRequired,
    selectedFilter: PropTypes.string.isRequired,
    onAvailableFilterChange: PropTypes.func.isRequired,
    onSelectedFilterChange: PropTypes.func.isRequired,
    onMoveField: PropTypes.func.isRequired,
    onClearAll: PropTypes.func.isRequired,
    adminMode: PropTypes.bool.isRequired,
    apsalarEnabled: PropTypes.bool.isRequired,
    exportAll: PropTypes.bool.isRequired,
    onExportAllChange: PropTypes.func.isRequired,
    presetOptions: PropTypes.arrayOf(presetShape).isRequired,
    onLoadPreset: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default FieldSelector;
